<script lang="ts">
import { computed, PropType, ref, watch } from "vue";
import { getLocalizedColumnName } from "@/composable/authorization/grantableInfos";
import {
  applyCheck,
  applyUncheck,
  isLockedByHierarchy,
  OPERATION_EXTRACTION,
} from "@/composable/authorization/useOperationTypeHierarchy";
import InputDateInterval from "@/components/common/provider/InputDateInterval.vue";
import CollapseMenuMultiselect from "@/components/common/CollapseMenuMultiselect.vue";
import services from "@/composable/services";
import { ReferenceScope } from "@/model/authorization/ReferenceScope";
import { BDropdown, BField, BTag, BTaginput } from "buefy";
import { ListColumnScope } from "@/types/front/ListColumnName";

/**
 * Composant table d'autorisations pour un datatype ( ou un référentiel ) .
 *
 * <p>Affiche un tableau de cases à cocher pour les opérations
 * ( Suppression / Dépôt / Extraction ) , avec une logique de hiérarchie
 * stricte ( ticket #521 ) déléguée au composable
 * {@link useOperationTypeHierarchy} :
 * <ul>
 *   <li>{@code delete} implique {@code depot + publication + extraction} ,
 *       cocher delete coche en cascade les autres ;</li>
 *   <li>{@code depot/publication} est une opération métier unique
 *       ( miroir auto ) ;</li>
 *   <li>une case verrouillée par un droit racine supérieur ne peut pas
 *       être décochée tant que le droit racine reste coché .</li>
 * </ul>
 *
 * <p><b>Architecture des données :</b> le composant lit
 * {@code props.authorization.operationTypes} ( source de vérité parent )
 * et émet {@code update:operation-type} avec le nouvel array . Aucun
 * état local mutable n'est gardé : tout passe par le parent , ce qui
 * permet au verrou hiérarchique d'être déterministe ( si le handler
 * refuse d'émettre , la prop reste inchangée , la checkbox re-rend la
 * valeur source via le {@code renderToken} qui force le re-mount ) .
 *
 * <p><b>Préservé tel quel ( hors scope #521 ) :</b> mécanisme
 * cross-datatype {@code hasDependencies} ( extraction verrouillée si
 * un autre datatype dépend de celui-ci ) , bloc référentiels
 * {@code b-taginput} + {@code CollapseMenuMultiselect} , gestion
 * {@code timeScope} via {@link InputDateInterval} .
 */
export default {
  name: "AuthorizationTableForOneDataType",
  components: { BTag, BTaginput, BDropdown, BField, CollapseMenuMultiselect, InputDateInterval },
  props: {
    authorization: Object,
    listColumnScope: {
      type: Object as PropType<ListColumnScope>,
      required: true,
    },
    data: Object,
    dataName: {
      type: String,
      default: "",
    },
    application: Object,
    applicationName: String,
    withTimeScope: Boolean,
    typeTimeScope: {
      type: String,
      default: "",
    },
    referenceScope: Array,
    hasDependencies: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    "update:propagate-changes",
    "update:date-value-interval",
    "update:operation-type",
    "update:has-extraction",
    "update:info-auth-ref-scope",
  ],
  setup(props, context) {
    const dropdownStates = ref([]);
    const locale = services.internationalisationService.getLocale();

    // ─── État réactif des opérations ─────────────────────────────────────────
    // Source de vérité unique : la prop parent . Convertit en Set pour
    // simplifier les checks ( .has au lieu de .includes/Object.keys ) et
    // s'aligner avec la signature du composable .
    const currentOps = computed<Set<string>>(() => {
      const ops = props.authorization?.operationTypes;
      if (!ops) return new Set();
      if (ops instanceof Set) return new Set(ops);
      return new Set(Array.isArray(ops) ? ops : Object.keys(ops));
    });

    // ─── Cross-datatype : extraction forcée si autre datatype dépend ──────
    // Mécanisme préservé du code historique ( hors scope ticket #521 ) :
    // quand un autre datatype référence celui-ci , son extraction est
    // implicitement requise pour résoudre les dépendances de données .
    const isForcedByCrossDatatype = (key: string): boolean =>
      key === OPERATION_EXTRACTION && props.hasDependencies.includes(props.data?.id);

    const isCheckboxChecked = (datatypeId: string, key: string): boolean => {
      if (key === OPERATION_EXTRACTION && props.hasDependencies.includes(datatypeId)) {
        return true;
      }
      return currentOps.value.has(key);
    };

    // ─── Verrou hiérarchie ( ticket #521 ) + verrou cross-datatype ────────
    const isCheckboxDisabled = (datatypeId: string, key: string): boolean =>
      (key === OPERATION_EXTRACTION && props.hasDependencies.includes(datatypeId))
      || isLockedByHierarchy(key, currentOps.value);

    // ─── Force re-render des cases en cas de toggle refusé ───────────────
    // Quand un toggle est refusé ( cas verrouillé ) , la prop parent ne
    // change pas , donc Vue ne re-rend pas la checkbox . On force le
    // re-mount via un token qui change dans la {@code :key} : Vue détruit
    // l'instance et la recrée , garantissant que l'affichage reflète
    // exactement la source de vérité ( indépendamment de tout état interne
    // que Buefy pourrait avoir conservé entre les renders ) .
    const renderToken = ref(0);

    const handleToggle = (key: string, newValue: boolean): void => {
      const ops = currentOps.value;
      const wasChecked = ops.has(key) || isForcedByCrossDatatype(key);
      // Cas no-op : event sans changement effectif , on ignore .
      if (newValue === wasChecked) return;

      if (newValue) {
        context.emit("update:operation-type", Array.from(applyCheck(key, ops)));
        return;
      }

      // Décochage : si verrouillé par la hiérarchie ou par cross-datatype ,
      // on refuse en forçant le re-render de la case avec la source parent .
      if (isCheckboxDisabled(props.data?.id, key)) {
        renderToken.value++;
        return;
      }
      context.emit("update:operation-type", Array.from(applyUncheck(key, ops)));
    };

    // ─── hasExtraction : émis vers le parent pour InputDateInterval ──────
    const hasExtraction = computed<boolean>(() => currentOps.value.has(OPERATION_EXTRACTION));
    const localHasExtraction = computed<boolean>(
      () => hasExtraction.value && Object.keys(props.authorization || {}).includes("timeScope"),
    );
    watch(
      localHasExtraction,
      (val) => context.emit("update:has-extraction", val),
      { immediate: true },
    );

    // ─── Bloc référentiels ( inchangé ) ──────────────────────────────────
    const selectedValuesReference = computed(() =>
      props.authorization?.requiredAuthorizations ? props.authorization?.requiredAuthorizations : {},
    );

    const displayNodeTagHierarchical: any = ref({});

    function displayNodeTag(referenceScopeNode: any, hierarchicalTagName: string) {
      if (referenceScopeNode.id === hierarchicalTagName) {
        if (referenceScopeNode.i18n) {
          displayNodeTagHierarchical.value[hierarchicalTagName] = referenceScopeNode.i18n[locale];
          return referenceScopeNode.i18n[locale];
        }
        else {
          displayNodeTagHierarchical.value[hierarchicalTagName] = referenceScopeNode.naturalKey;
          return referenceScopeNode.naturalKey;
        }
      }
      else {
        for (let i = 0; referenceScopeNode.children.length > i; i++) {
          displayNodeTag(referenceScopeNode.children[i], hierarchicalTagName);
        }
      }
    }

    function getTimeScopeFormat() {
      const timestampFieldName: string = props.typeTimeScope;
      return (
        props.application?.configuration?.dataDescription?.[props.dataName]
          ?.componentDescriptions?.[timestampFieldName]?.checker?.pattern || "dd/MM:yyyy"
      );
    }

    function rootReferenceScopeSelected(hierarchicalTagName: string, referenceScopesByDataId: string) {
      const reference = props.referenceScope.find(
        ref => ref.datatype === props.dataName && ref.id === referenceScopesByDataId,
      );

      if (reference) {
        const node = reference.nodes.find((n: any) => n.id === hierarchicalTagName);

        if (node) {
          return node.i18n ? node.i18n[locale] : node.naturalKey;
        }

        reference.nodes.forEach((n: any) => displayNodeTag(n, hierarchicalTagName));
      }

      return (
        displayNodeTagHierarchical.value[hierarchicalTagName]
        || hierarchicalTagName.split("K").pop()
      );
    }

    return {
      selectedValuesReference,
      locale,
      handleToggle,
      hasExtraction,
      localHasExtraction,
      renderToken,
      rootReferenceScopeSelected,
      displayNodeTagHierarchical,
      isCheckboxChecked,
      isCheckboxDisabled,
      isLockedByHierarchy: (key: string) => isLockedByHierarchy(key, currentOps.value),
      dropdownStates,
      getTimeScopeFormat,
      localizedColumnName: getLocalizedColumnName,
    };
  },
  computed: {
    ReferenceScope() {
      return ReferenceScope;
    },
  },
};
</script>

<template>
  <div>
    <div class="columns">
      <b-field
        v-for="referenceScopesByData in referenceScope"
        :key="referenceScopesByData.id"
        :label="referenceScopesByData.i18n[locale] || referenceScopesByData.id"
        :aria-label="referenceScopesByData.i18n[locale] || referenceScopesByData.id"
        class="column is-3"
      >
        <b-dropdown
          :ref="referenceScopesByData.id"
          :triggers="['focus']"
          expanded
          max-height="300"
          open-on-focus
          scrollable
          width="500"
        >
          <template #trigger="{ active }">
            <b-taginput
              v-model="selectedValuesReference[referenceScopesByData.id]"
              :icon-right="active ? 'angle-up' : 'angle-down'"
              placeholder="Select some tags"
            >
              <template #selected="props">
                <b-tag
                  v-for="(tag, index) in props.tags"
                  :id="referenceScopesByData.id + '-_-' + tag"
                  :key="index"
                  rounded
                  type="is-dark"
                  :aria-label="rootReferenceScopeSelected(tag, referenceScopesByData.id)"
                >
                  {{ rootReferenceScopeSelected(tag, referenceScopesByData.id) }}
                </b-tag>
              </template>
            </b-taginput>
          </template>
          <CollapseMenuMultiselect
            v-for="option in referenceScopesByData.nodes"
            :id="option.id"
            :key="option.id"
            :datatype="data.id"
            :is-checked="true"
            :option="option"
            :reference-scope="
              new ReferenceScope(
                referenceScopesByData,
                authorization?.requiredAuthorizations?.[referenceScopesByData.id]
              )
            "
            :required-authorizations="
              authorization?.requiredAuthorizations?.[referenceScopesByData.id]
            "
            :scope="referenceScopesByData.id"
            @select-menu-item="
              $emit('update:info-auth-ref-scope', { ...$event, datatype: data.id })
            "
            @propagate-changes="
              $emit('update:propagate-changes', {
                changes: $event,
                datatype: data.id,
                scope: referenceScopesByData.id,
              })
            "
          />
        </b-dropdown>
      </b-field>
    </div>
    <div class="columns tableCheckboxes">
      <table class="table is-bordered is-striped is-narrow is-hoverable">
        <thead>
          <tr>
            <th
              v-for="(column, key) in listColumnScope"
              :key="`${dataName}-${key}`"
              :label="localizedColumnName(column)"
              :aria-label="localizedColumnName(column)"
            >
              {{ localizedColumnName(column) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              v-for="(column, key) in listColumnScope"
              :key="`${dataName}-${key}`"
              :aria-label="`${dataName}-${key}`"
              :class="{ 'is-locked-by-hierarchy': isLockedByHierarchy(key) }"
            >
              <b-checkbox
                :key="`${dataName}-${key}-${renderToken}`"
                :model-value="isCheckboxChecked(data.id, key)"
                :disabled="isCheckboxDisabled(data.id, key)"
                :aria-label="isCheckboxChecked(data.id, key)"
                @update:model-value="handleToggle(key, $event)"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <InputDateInterval
        v-if="withTimeScope"
        :authorization="authorization"
        :format="getTimeScopeFormat()"
        :has-extraction="hasExtraction || localHasExtraction"
        :input-type="typeTimeScope"
        class="column is-3"
        @update:date-value-interval="$emit('update:date-value-interval', $event)"
      />
    </div>
  </div>
</template>

<style scoped>
.is-locked-by-hierarchy {
  opacity: 0.55;
  cursor: not-allowed;
}
.is-locked-by-hierarchy :deep(.b-checkbox) {
  cursor: not-allowed;
}
</style>
